import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useTickets, useTicketRequestTypes } from "hooks/crm/useTickets";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import { ticketRef } from "utilities/ticketNumber";
import RecordTable from "components/Records/RecordTable";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import RelativeTime from "components/RelativeTime";
import UserAvatar from "components/UserAvatar";
import ContainerScopeNotice from "components/ContainerScopeNotice";
import TicketStatusPill from "components/Tickets/TicketStatusPill";
import PriorityIndicator from "components/Tickets/PriorityIndicator";
import TicketCreateModal from "components/Tickets/TicketCreateModal";
import SlaBadge from "components/Tickets/SlaBadge";
import { Inbox, Plus } from "lucide-react";

const FIELDS = filterFieldsFor("companyTickets");

const STATE_TABS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

function humanize(key) {
  if (!key) return "";
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * TicketsTab (SOUND-493, rebuilt SOUND-645) — the company record's ticket queue.
 *
 * Same table as /tickets, scoped to this company and without the page header
 * (the hub owns that). It replaces a fetch capped at limit=100 whose search and
 * status filter then ran CLIENT-SIDE over those rows — a company past 100
 * tickets was filtering a truncated set with nothing to say so.
 */
function TicketsTabInner({ companyId, companyName, isPartnerView }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const internal = isInternal(user);
  const { filters, set } = useSearch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);

  const sortParam = filters.sort || "-updatedAt";
  const columnSort = filters.sort === "sla" ? "" : sortParam;

  // companyId is FORCED by the record. The API expands containers server-side —
  // a partner resolves to its attributed clients, a parent to itself plus
  // children (resolveViewedCompanyScope).
  const { tickets, pagination, loading, error, refetch } = useTickets({
    filters: { ...filters, companyId },
    page,
    limit,
    sort: sortParam,
    include: internal ? "deps" : undefined,
  });

  const { requestTypes } = useTicketRequestTypes();
  const rtLabel = useMemo(() => {
    const map = {};
    requestTypes.forEach((rt) => (map[rt.key] = rt.label));
    return (key) => map[key] || humanize(key);
  }, [requestTypes]);

  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, FIELDS, {
    ignore: ["sort", "assigned"],
  });

  // Container companies (partner/parent) hold rows belonging to related
  // companies; derived from the data so the Client column shows only where it
  // distinguishes something.
  const aggregated = tickets.some((t) => t.companyId && t.companyId !== companyId);

  const columns = useMemo(
    () => [
      {
        key: "number",
        header: "#",
        width: "10%",
        sortKey: "number",
        defaultSortDir: "desc",
        className: "font-mono text-[11px] tabular-nums text-muted-foreground",
        cellClassName: (t) =>
          internal && (t.blocks || []).length ? "border-l-2 border-l-brand" : "",
        exportValue: (t) => t.number,
        cell: (t) => <>{ticketRef(t.number)}</>,
      },
      internal && {
        key: "priority",
        header: "Priority",
        width: "11%",
        exportValue: (t) => t.priority || "",
        cell: (t) => <PriorityIndicator priority={t.priority} />,
      },
      aggregated && {
        key: "client",
        header: "Client",
        width: "16%",
        exportValue: (t) => t.companyName || "",
        cell: (t) => <div className="truncate">{t.companyName || "—"}</div>,
      },
      {
        key: "title",
        header: "Title",
        width: aggregated ? "30%" : "43%",
        sortKey: "title",
        truncate: false,
        exportValue: (t) => t.title || "",
        cell: (t) => {
          const secondLine = t.requestType ? rtLabel(t.requestType) : "";
          return (
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{t.title}</span>
                {t.kind === "task" && <span className="badge badge-muted shrink-0">Task</span>}
              </div>
              {secondLine && (
                <div className="truncate text-[11px] text-muted-foreground">{secondLine}</div>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        width: "13%",
        sortKey: "status",
        exportValue: (t) => t.status || "",
        cell: (t) => <TicketStatusPill status={t.status} statusCategory={t.statusCategory} />,
      },
      internal && {
        key: "sla",
        header: "SLA",
        width: "10%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        exportValue: (t) => t.sla?.dueAt || "",
        cell: (t) =>
          t.sla && t.sla.policyId ? (
            <SlaBadge ticket={t} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      internal && {
        key: "assignee",
        header: "Assignee",
        width: "8%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        exportValue: (t) => t.assignee?.displayName || t.assignee?.email || "",
        cell: (t) =>
          t.assignee ? (
            <UserAvatar
              user={{
                name: t.assignee.displayName || t.assignee.email,
                email: t.assignee.email,
              }}
              size={22}
            />
          ) : (
            <span className="text-muted-foreground" title="Unassigned">
              —
            </span>
          ),
      },
      {
        key: "updatedAt",
        header: "Updated",
        width: "10%",
        sortKey: "updatedAt",
        defaultSortDir: "desc",
        className: "text-muted-foreground",
        exportValue: (t) => t.updatedAt || "",
        cell: (t) => <RelativeTime value={t.updatedAt} />,
      },
    ],
    [internal, aggregated, rtLabel]
  );

  return (
    <div className="space-y-4">
      {(isPartnerView || aggregated) && (
        <ContainerScopeNotice
          variant={isPartnerView ? "partner" : "parent"}
          noun="tickets"
        />
      )}

      <ListSearchBar
        value={filters.search || ""}
        onChange={(v) => set("search", v)}
        placeholder="Search title, description, or SC-number"
      >
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {STATE_TABS.map((s) => {
            const active = (filters.state || "open") === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => set("state", s.value)}
                className={`px-3 py-2 text-xs font-semibold transition-colors ${
                  active
                    ? "bg-brand/[0.14] text-foreground"
                    : "bg-card text-muted-foreground hover:text-foreground"
                } ${s.value !== "all" ? "border-l border-border" : ""}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <button className="btn btn-primary ml-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New ticket
        </button>
      </ListSearchBar>

      <RecordTable
        columns={columns}
        rows={tickets}
        rowKey={(t) => t.ticketId}
        rowClassName={(t) =>
          internal && (t.blockedByOpen || []).length ? "bg-muted/30" : ""
        }
        onRowClick={(t) => navigate(`/tickets/${t.ticketId}`)}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={activeFilterCount > 0}
        sort={columnSort}
        onSortChange={(next) => {
          set("sort", next);
          setPage(1);
        }}
        noMatch={{ icon: Inbox, heading: "No matching tickets" }}
        empty={{
          icon: Inbox,
          heading: isPartnerView
            ? "No tickets across this partner's attributed clients yet"
            : "No tickets for this company yet",
          errorText: "Failed to load tickets.",
          action: (
            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New ticket
            </button>
          ),
        }}
      />

      <ListPagination
        page={pagination.page || page}
        totalPages={pagination.totalPages || 0}
        total={pagination.total || 0}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />

      <TicketCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        presetCompanyId={companyId}
        presetCompanyName={companyName}
        onCreated={(t) => {
          setCreateOpen(false);
          if (t?.ticketId) navigate(`/tickets/${t.ticketId}`);
          else refetch();
        }}
      />
    </div>
  );
}

export default function TicketsTab({ companyId, companyName, isPartnerView = false }) {
  return (
    <SearchProvider namespace="companyTickets">
      <TicketsTabInner
        companyId={companyId}
        companyName={companyName}
        isPartnerView={isPartnerView}
      />
    </SearchProvider>
  );
}
